import type { paths } from "@/api/schema.generated";
import { platformGraph, getAccessToken, platformGraphBaseUrl } from "@/api/platform-graph";
import { EnvelopeError, unwrapEnvelope } from "@/api/errors";

type Ok<T> = T extends { responses: { 200: { content: { "application/json": { data: infer D } } } } } ? D : never;
export type Capabilities = Ok<paths["/v1/staff-requisitions/capabilities"]["get"]>;
export type Requisition = Ok<paths["/v1/me/staff-requisitions"]["get"]>["items"][number];
export type RequisitionDetail = Ok<paths["/v1/me/staff-requisitions/{id}"]["get"]>;
export type RequisitionStatus = Requisition["status"];
export type CreateBody = paths["/v1/me/staff-requisitions"]["post"]["requestBody"]["content"]["application/json"];
export type StaffDetails = Extract<CreateBody, { type: "personel" }>["details"];
export type InternDetails = Extract<CreateBody, { type: "stajyer" }>["details"];
export type ExportFormat = "pdf" | "xlsx";

export type Command =
  | { kind: "create"; body: CreateBody; submit: boolean }
  | { kind: "edit"; id: string; body: CreateBody & { version: number }; submit: boolean }
  | { kind: "submit"; id: string; version: number }
  | { kind: "cancel"; id: string; version: number; reason: string };
/** submitError: taslak yazıldı, ama aynı komuttaki gönderim reddedildi (kod). */
export type CommandResult = { id: string; submitted: boolean; submitError: string | null };

export const isInternDetails = (details: Requisition["details"]): details is InternDetails => "education_field" in details;

export const fetchCapabilities = () => unwrapEnvelope(platformGraph.GET("/v1/staff-requisitions/capabilities"));
export function listRequisitions(self: boolean, cursor: string | null) {
  const init = { params: { query: { limit: 30, ...(cursor ? { cursor } : {}) } } };
  return unwrapEnvelope(self ? platformGraph.GET("/v1/me/staff-requisitions", init) : platformGraph.GET("/v1/staff-requisitions", init));
}
export function fetchRequisition(self: boolean, id: string) {
  const init = { params: { path: { id } } };
  return unwrapEnvelope(self ? platformGraph.GET("/v1/me/staff-requisitions/{id}", init) : platformGraph.GET("/v1/staff-requisitions/{id}", init));
}

async function submitCurrent(id: string, key: string): Promise<boolean> {
  // Sürüm, taslak yazımından sonra sunucudan okunur: istemci sürümü tahmin ederse
  // belirsiz bir yanıttan sonraki tekrar yanlış sürümle çakışma alır.
  const { request } = await fetchRequisition(true, id);
  // Önceki deneme gönderimi tamamlamışsa ikinci gönderim gövdesi farklı olur ve
  // idempotency çakışması verir; durum zaten kararı söyler.
  if (request.status !== "draft") return request.status === "pending";
  const header = { "x-idempotency-key": `${key}:submit` };
  await unwrapEnvelope(platformGraph.POST("/v1/me/staff-requisitions/{id}/submit", { params: { header, path: { id } }, body: { version: request.version } }));
  return true;
}

export async function executeCommand(command: Command, key: string): Promise<CommandResult> {
  const header = { "x-idempotency-key": key };
  if (command.kind === "submit") {
    await unwrapEnvelope(platformGraph.POST("/v1/me/staff-requisitions/{id}/submit", { params: { header, path: { id: command.id } }, body: { version: command.version } }));
    return { id: command.id, submitted: true, submitError: null };
  }
  if (command.kind === "cancel") {
    await unwrapEnvelope(platformGraph.POST("/v1/me/staff-requisitions/{id}/cancel", { params: { header, path: { id: command.id } }, body: { version: command.version, reason: command.reason } }));
    return { id: command.id, submitted: false, submitError: null };
  }
  const { id } = command.kind === "create"
    ? await unwrapEnvelope(platformGraph.POST("/v1/me/staff-requisitions", { params: { header }, body: command.body }))
    : await unwrapEnvelope(platformGraph.PUT("/v1/me/staff-requisitions/{id}", { params: { header, path: { id: command.id } }, body: command.body }));
  if (!command.submit) return { id, submitted: false, submitError: null };
  // Taslak kaydedildi; gönderim hatası komutu başarısız saymaz. Aksi hâlde aynı
  // formdan tekrar gönderim yeni anahtarla ikinci bir taslak açardı.
  try { return { id, submitted: await submitCurrent(id, key), submitError: null }; }
  catch (error) { return { id, submitted: false, submitError: error instanceof EnvelopeError ? error.primary?.code ?? "unknown" : "unknown" }; }
}

export async function downloadExport(id: string, requisitionNo: string, format: ExportFormat, locale: "tr-TR" | "en-US", isCurrent: () => boolean) {
  const token = getAccessToken();
  const response = await fetch(`${platformGraphBaseUrl}/v1/staff-requisitions/${encodeURIComponent(id)}/export?format=${format}&locale=${locale}`, { headers: token ? { authorization: `Bearer ${token}` } : {}, credentials: "include", cache: "no-store" });
  if (!response.ok) { await unwrapEnvelope(Promise.resolve({ error: await response.json().catch(() => undefined), response })); return; }
  const blob = await response.blob(); if (!isCurrent()) return;
  const url = URL.createObjectURL(blob);
  try { const link = document.createElement("a"); link.href = url; link.download = `${requisitionNo}.${format}`; document.body.appendChild(link); link.click(); link.remove(); }
  finally { URL.revokeObjectURL(url); }
}
